import { createElementVNode as _createElementVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue";
// Styles
import "./VInfiniteCarousel.css";

// Components
import { VIconBtn } from "../../components/VIconBtn/index.js"; // Composables
import { makeComponentProps } from "../../composables/component.js";
import { makeDimensionProps, useDimension } from "../../composables/dimensions.js";
import { useRtl } from "../../composables/locale.js";
import { useResizeObserver } from "../../composables/resizeObserver.js";
import { makeTagProps } from "../../composables/tag.js";
import { useVirtualFocus } from "../../composables/virtualFocus.js"; // Utilities
import { onMounted, onScopeDispose, onUpdated, ref, shallowRef, toRef, watch } from 'vue';
import { convertToUnit, flattenFragments, focusableChildren, genericComponent, isBoolean, isString, PREFERS_REDUCED_MOTION, propsFactory, useRender } from "../../util/index.js"; // Types
const STEP_DECAY = 120;
const DRAG_THRESHOLD = 4;
const MAX_COPIES = 20;
export const makeVInfiniteCarouselProps = propsFactory({
  direction: {
    type: String,
    default: 'horizontal'
  },
  reverse: Boolean,
  paused: Boolean,
  pauseOnHover: Boolean,
  draggable: {
    type: Boolean,
    default: undefined
  },
  wheel: Boolean,
  speed: {
    type: [Number, String],
    default: 35
  },
  mask: [Boolean, Number, String],
  gap: [Number, String],
  shiftDistance: {
    type: [Number, String],
    default: '20%'
  },
  showArrows: {
    type: [Boolean, String],
    validator: v => isBoolean(v) || v === 'hover'
  },
  prevIcon: {
    type: [Boolean, String, Function, Object],
    default: '$prev'
  },
  nextIcon: {
    type: [Boolean, String, Function, Object],
    default: '$next'
  },
  ...makeComponentProps(),
  ...makeDimensionProps(),
  ...makeTagProps()
}, 'VInfiniteCarousel');
export const VInfiniteCarousel = genericComponent()({
  name: 'VInfiniteCarousel',
  props: makeVInfiniteCarouselProps(),
  setup(props, {
    slots
  }) {
    const {
      isRtl
    } = useRtl();
    const {
      dimensionStyles
    } = useDimension(props);
    const containerRef = ref();
    const trackRef = ref();
    const probeRef = ref();
    const items = shallowRef([]);
    const copies = shallowRef(1);
    const isDragging = shallowRef(false);
    const isHovering = shallowRef(false);
    const viewportSize = shallowRef(0);
    const loopDistance = shallowRef(0);
    const isVertical = toRef(() => props.direction === 'vertical');
    const isDraggable = toRef(() => props.draggable ?? PREFERS_REDUCED_MOTION());
    const isMirrored = toRef(() => isRtl.value && !isVertical.value);
    const loopOffset = toRef(() => isMirrored.value ? loopDistance.value : -loopDistance.value);
    const speed = toRef(() => Math.max(0, Number(props.speed) || 0));
    const loopDuration = toRef(() => speed.value ? loopDistance.value / speed.value * 1000 : 1000);
    const viewportRef = shallowRef();
    const contentRef = shallowRef();
    useResizeObserver(viewportRef, onResize);
    useResizeObserver(contentRef, onResize);
    function onResize() {
      const viewport = viewportRef.value;
      const group = contentRef.value;
      if (!viewport || !group) return;
      const viewportStyles = getComputedStyle(viewport);
      const groupStyles = getComputedStyle(group);
      const [groupSize, outerSize, paddingStart, paddingEnd, gap] = isVertical.value ? [group.offsetHeight, viewport.clientHeight, viewportStyles.paddingTop, viewportStyles.paddingBottom, groupStyles.rowGap] : [group.offsetWidth, viewport.clientWidth, viewportStyles.paddingLeft, viewportStyles.paddingRight, groupStyles.columnGap];
      const gapSize = parseFloat(gap) || 0;
      const endPadding = parseFloat(paddingEnd);
      viewportSize.value = outerSize - parseFloat(paddingStart) - endPadding;
      loopDistance.value = groupSize + gapSize;
      copies.value = loopDistance.value > 0 ? Math.min(MAX_COPIES, 1 + Math.ceil((viewportSize.value + endPadding + gapSize) / loopDistance.value)) : 1;
    }
    let animation;
    function syncAnimation() {
      cancelStep();
      const track = trackRef.value;
      const previousShift = currentShift();
      animation?.cancel();
      animation = undefined;
      if (!track || !loopDuration.value) return;
      const end = isVertical.value ? `translateY(${loopOffset.value}px)` : `translateX(${loopOffset.value}px)`;
      animation = track.animate([{
        transform: 'none'
      }, {
        transform: end
      }], {
        duration: loopDuration.value,
        direction: props.reverse ? 'reverse' : 'normal',
        iterations: Infinity,
        easing: 'linear'
      });
      seek(previousShift);
      if (isPaused()) animation.pause();
    }
    watch([isVertical, speed, () => props.reverse, loopOffset], syncAnimation, {
      flush: 'post'
    });
    watch(() => props.direction, onResize, {
      flush: 'post'
    });
    watch(() => props.paused, paused => {
      if (paused) animation?.pause();else resume();
    });
    function currentShift() {
      if (!animation) return 0;

      // read from the animation, props already hold the values it is about to be rebuilt with
      const {
        duration,
        direction
      } = animation.effect.getTiming();
      const progress = Number(animation.currentTime ?? 0) % Number(duration) / Number(duration);
      return (direction === 'reverse' ? 1 - progress : progress) * loopDistance.value;
    }
    function seek(value) {
      const duration = loopDuration.value;
      if (!animation || !loopDistance.value) return;
      const wrapped = (value % loopDistance.value + loopDistance.value) % loopDistance.value;
      const progress = wrapped / loopDistance.value;
      animation.currentTime = Math.min((props.reverse ? 1 - progress : progress) * duration, duration - 1);
    }
    let pendingShift = 0;
    let stepFrame = 0;
    let lastStepTime = 0;
    function advanceStep(now) {
      const decayed = pendingShift * (1 - Math.exp(-(now - lastStepTime) / STEP_DECAY));
      lastStepTime = now;
      pendingShift -= decayed;
      seek(currentShift() + decayed);
      stepFrame = Math.abs(pendingShift) > 0.5 ? requestAnimationFrame(advanceStep) : 0;
      if (!stepFrame) pendingShift = 0;
    }
    function cancelStep() {
      cancelAnimationFrame(stepFrame);
      stepFrame = 0;
      pendingShift = 0;
    }
    function resolveShiftDistance() {
      const value = props.shiftDistance;
      if (isString(value) && value.endsWith('%')) {
        return parseFloat(value) / 100 * viewportSize.value;
      }
      return (isVertical.value ? probeRef.value?.offsetHeight : probeRef.value?.offsetWidth) ?? 0;
    }
    function step(direction) {
      if (!animation || !loopDistance.value) return;
      shiftBy(direction * resolveShiftDistance());
    }
    function shiftBy(amount) {
      if (PREFERS_REDUCED_MOTION()) {
        seek(currentShift() + amount);
        return;
      }
      pendingShift += amount;
      if (!stepFrame) {
        lastStepTime = performance.now();
        stepFrame = requestAnimationFrame(advanceStep);
      }
    }
    function reveal(el) {
      const group = contentRef.value;
      if (!group) return;
      cancelStep();
      const [start, size] = isVertical.value ? [el.offsetTop - group.offsetTop, el.offsetHeight] : isMirrored.value ? [group.offsetLeft + group.offsetWidth - el.offsetLeft - el.offsetWidth, el.offsetWidth] : [el.offsetLeft - group.offsetLeft, el.offsetWidth];
      const shift = currentShift();
      if (start < shift) seek(start);else if (start + size > shift + viewportSize.value) seek(start + size - viewportSize.value);
    }
    const virtualFocus = useVirtualFocus(() => items.value.map((el, id) => ({
      id,
      el
    })), {
      control: () => containerRef.value,
      circular: true,
      onHighlight(id) {
        const el = items.value[id];
        if (!el) return;
        reveal(el);
        el.focus();
      }
    });
    let hasFocusableItems = false;
    function onKeydown(e) {
      const [previousKey, nextKey] = isVertical.value ? ['ArrowUp', 'ArrowDown'] : isRtl.value ? ['ArrowRight', 'ArrowLeft'] : ['ArrowLeft', 'ArrowRight'];
      if (!hasFocusableItems) {
        if (e.key === previousKey) step(-1);else if (e.key === nextKey) step(1);else return;
      } else if (e.key === previousKey) virtualFocus.prev();else if (e.key === nextKey) virtualFocus.next();else if (e.key === 'Home') virtualFocus.first();else if (e.key === 'End') virtualFocus.last();else return;
      e.preventDefault();
    }
    function isPaused() {
      const container = containerRef.value;
      return props.paused || !speed.value || isDragging.value || props.pauseOnHover && isHovering.value || PREFERS_REDUCED_MOTION() || !!container?.matches(':focus-visible') || !!container?.querySelector(':focus-visible');
    }
    function onPointerenter() {
      isHovering.value = true;
      if (props.pauseOnHover) animation?.pause();
    }
    function onPointerleave() {
      isHovering.value = false;
      resume();
    }
    function resume() {
      if (!isPaused()) animation?.play();
    }
    let dragOrigin = null;
    let hasDragged = false;
    function onPointerdown(e) {
      if (!isDraggable.value || e.button !== 0 || !animation) return;
      if (e.target.closest('.v-infinite-carousel__controls')) return;
      cancelStep();
      dragOrigin = {
        coordinate: isVertical.value ? e.clientY : e.clientX,
        shift: currentShift()
      };
      hasDragged = false;
      animation.pause();
    }
    function onPointermove(e) {
      if (!dragOrigin) return;
      const delta = (isVertical.value ? e.clientY : e.clientX) - dragOrigin.coordinate;
      if (!isDragging.value) {
        if (Math.abs(delta) < DRAG_THRESHOLD) return;
        isDragging.value = true;
        hasDragged = true;
        containerRef.value?.setPointerCapture(e.pointerId);
      }
      seek(dragOrigin.shift + (isMirrored.value ? delta : -delta));
    }
    function onPointerup() {
      dragOrigin = null;
      isDragging.value = false;
      resume();
    }
    function onDragstart(e) {
      if (dragOrigin) e.preventDefault();
    }
    function onWheel(e) {
      const delta = isVertical.value ? e.deltaY : e.deltaX || (e.shiftKey ? e.deltaY : 0);
      if (!props.wheel || !delta || !animation || !loopDistance.value) return;
      e.preventDefault();
      shiftBy((isMirrored.value ? -delta : delta) * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? viewportSize.value : 1));
    }
    function onClickCapture(e) {
      if (!hasDragged) return;
      hasDragged = false;
      e.preventDefault();
      e.stopPropagation();
    }
    function onFocusin(e) {
      const target = e.target;
      const item = items.value.find(el => el.contains(target));

      // keyboard only, so clicking an item half under the fade does not jump the strip
      if (item && target.matches(':focus-visible')) reveal(item);
      if (isPaused()) animation?.pause();
    }
    function onFocusout(e) {
      if (containerRef.value?.contains(e.relatedTarget)) return;
      virtualFocus.clear();
      resume();
    }
    function collectItems() {
      items.value = Array.from(contentRef.value?.children ?? []).filter(el => !el.classList.contains('v-infinite-carousel__separator'));

      // sticky: the tabindex set below keeps this true even if the content later loses its focusables
      hasFocusableItems = !!contentRef.value && focusableChildren(contentRef.value, false).length > 0;
      if (hasFocusableItems) {
        items.value.forEach(item => {
          item.tabIndex = -1;
        });
      }
      trackRef.value?.querySelectorAll('.v-infinite-carousel__group[aria-hidden]').forEach(copy => focusableChildren(copy).forEach(el => {
        el.tabIndex = -1;
      }));
    }
    onMounted(collectItems);
    onUpdated(collectItems);
    onScopeDispose(() => {
      animation?.cancel();
      cancelStep();
    });
    function groupChildren() {
      const children = slots.default?.() ?? [];
      if (!slots.separator) return children;
      return flattenFragments(children).flatMap((child, index) => [child, _createElementVNode("div", {
        "key": `separator-${index}`,
        "class": "v-infinite-carousel__separator",
        "aria-hidden": "true"
      }, [slots.separator()])]);
    }
    useRender(() => {
      const previousProps = {
        icon: isMirrored.value ? props.nextIcon : props.prevIcon,
        class: 'v-infinite-carousel__prev',
        variant: 'text',
        tabindex: -1,
        'aria-hidden': true,
        onMousedown: e => e.preventDefault(),
        onClick: () => step(-1)
      };
      const nextProps = {
        icon: isMirrored.value ? props.prevIcon : props.nextIcon,
        class: 'v-infinite-carousel__next',
        variant: 'text',
        tabindex: -1,
        'aria-hidden': true,
        onMousedown: e => e.preventDefault(),
        onClick: () => step(1)
      };
      return _createVNode(props.tag, {
        "ref": containerRef,
        "class": _normalizeClass(['v-infinite-carousel', {
          'v-infinite-carousel--vertical': isVertical.value,
          'v-infinite-carousel--mask': !!props.mask,
          'v-infinite-carousel--draggable': isDraggable.value && copies.value > 1,
          'v-infinite-carousel--dragging': isDragging.value,
          'v-infinite-carousel--show-arrows-on-hover': props.showArrows === 'hover'
        }, props.class]),
        "style": _normalizeStyle([{
          '--v-infinite-carousel-mask-size': convertToUnit(isBoolean(props.mask) ? undefined : props.mask),
          '--v-infinite-carousel-gap': convertToUnit(props.gap),
          '--v-infinite-carousel-shift': convertToUnit(props.shiftDistance)
        }, dimensionStyles.value, props.style]),
        "role": "group",
        "tabindex": virtualFocus.highlightedId.value == null ? 0 : -1,
        "onFocusin": onFocusin,
        "onFocusout": onFocusout,
        "onKeydown": onKeydown,
        "onPointerenter": onPointerenter,
        "onPointerleave": onPointerleave,
        "onPointerdown": onPointerdown,
        "onPointermove": onPointermove,
        "onPointerup": onPointerup,
        "onPointercancel": onPointerup,
        "onDragstart": onDragstart,
        "onWheel": onWheel,
        "onClickCapture": onClickCapture
      }, {
        default: () => [_createElementVNode("div", {
          "ref": viewportRef,
          "class": "v-infinite-carousel__viewport"
        }, [_createElementVNode("div", {
          "ref": trackRef,
          "class": "v-infinite-carousel__track",
          "style": {
            translate: isVertical.value ? `0 ${loopOffset.value}px` : `${loopOffset.value}px`
          }
        }, [_createElementVNode("div", {
          "ref": contentRef,
          "class": "v-infinite-carousel__group"
        }, [groupChildren()]), _createElementVNode("div", {
          "class": "v-infinite-carousel__group v-infinite-carousel__group--leading",
          "aria-hidden": "true"
        }, [groupChildren()]), Array.from({
          length: copies.value - 1
        }, (_, index) => _createElementVNode("div", {
          "key": index,
          "class": "v-infinite-carousel__group",
          "aria-hidden": "true"
        }, [groupChildren()]))])]), _createElementVNode("div", {
          "ref": probeRef,
          "class": "v-infinite-carousel__probe"
        }, null), props.showArrows && _createElementVNode("div", {
          "class": "v-infinite-carousel__controls"
        }, [slots.prev ? slots.prev({
          props: previousProps
        }) : _createVNode(VIconBtn, previousProps, null), slots.next ? slots.next({
          props: nextProps
        }) : _createVNode(VIconBtn, nextProps, null)])]
      });
    });
    return {
      slide: direction => step(direction === 'next' ? 1 : -1)
    };
  }
});
//# sourceMappingURL=VInfiniteCarousel.js.map